import React, { useCallback, useEffect, useState } from "react";
import { Button, Container, Spinner } from "react-bootstrap";
import { ethers } from "ethers";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MdToken, MdOpenInNew, MdRefresh } from "react-icons/md";
import ContractDetails from "../../Contracts/ContractDetails";
import GetChainId from "../../Common/GetChainId";
import { Data } from "../../Common/Data";
import "./TokenRateOnChain.css";

const RATE_DECIMALS = 18;

function rateOnChainToDisplay(bn) {
  if (bn == null) return "—";
  try {
    let s = ethers.utils.formatUnits(bn, RATE_DECIMALS);
    if (s.includes(".")) {
      s = s.replace(/0+$/, "").replace(/\.$/, "");
    }
    return s || "0";
  } catch {
    return "—";
  }
}

function displayRateToOnChain(input) {
  const t = String(input).trim().replace(",", ".");
  if (t === "") throw new Error("empty");
  return ethers.utils.parseUnits(t, RATE_DECIMALS);
}

const TokenRateOnChain = () => {
  const account = useSelector((state) => state.accounts.value);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [contractOwner, setContractOwner] = useState(null);
  const [currentRate, setCurrentRate] = useState(null);
  const [newRateStr, setNewRateStr] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const loadChainData = useCallback(async () => {
    const { ethereum } = window;
    if (!ethereum || !account) {
      setLoading(false);
      return;
    }
    const ok = await GetChainId();
    if (!ok) {
      setLoading(false);
      return;
    }

    const provider = new ethers.providers.Web3Provider(ethereum);
    const contractRead = new ethers.Contract(
      ContractDetails.contract,
      ContractDetails.contractABI,
      provider
    );

    try {
      const [owner_, rate_] = await Promise.all([
        contractRead.owner(),
        contractRead.getTokenRate(),
      ]);
      setContractOwner(owner_);
      setCurrentRate(rate_);
    } catch (e) {
      console.error(e);
      toast.error(e?.reason || e?.message || "Failed to read contract");
      setCurrentRate(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [account]);

  useEffect(() => {
    setLoading(true);
    loadChainData();
  }, [loadChainData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadChainData();
  };

  const handleSetTokenRate = async () => {
    if (
      !contractOwner ||
      !account ||
      contractOwner.toLowerCase() !== account.toLowerCase()
    ) {
      toast.error("Only the contract owner wallet can update the rate.");
      return;
    }
    let newRateBn;
    try {
      newRateBn = displayRateToOnChain(newRateStr);
    } catch {
      toast.error(
        "Enter a valid number (e.g. 0.05). Up to 18 decimal places."
      );
      return;
    }

    const { ethereum } = window;
    if (!ethereum || !account) {
      toast.error("Connect your wallet first.");
      return;
    }
    const ok = await GetChainId();
    if (!ok) return;

    setSavingRate(true);
    try {
      const provider = new ethers.providers.Web3Provider(ethereum);
      const signer = provider.getSigner();
      const contractWrite = new ethers.Contract(
        ContractDetails.contract,
        ContractDetails.contractABI,
        signer
      );
      const tx = await contractWrite.setTokenRate(newRateBn);
      toast.info("Confirm in your wallet…");
      await tx.wait();
      toast.success("Rate saved.");
      setNewRateStr("");
      await loadChainData();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.reason ||
          e?.data?.message ||
          e?.message ||
          "Could not save. Use the owner wallet."
      );
    } finally {
      setSavingRate(false);
    }
  };

  if (!account) {
    return (
      <Container fluid className="p-0">
        <div className="tr-page">
          <div className="tr-empty">
            <div className="tr-empty-icon" aria-hidden>
              <MdToken />
            </div>
            <h2>Connect wallet</h2>
            <p>
              Use the button in the top bar to connect your admin wallet, then
              you can view and update the token rate.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="tr-page">
          <div className="tr-loading">
            <Spinner animation="border" variant="primary" role="status" />
            <p>Loading rate from contract…</p>
          </div>
        </div>
      </Container>
    );
  }

  const ownerMatch =
    contractOwner &&
    account &&
    contractOwner.toLowerCase() === account.toLowerCase();

  const displayCurrent = rateOnChainToDisplay(currentRate);

  return (
    <Container fluid className="p-0">
      <div className="tr-page">
        <header className="tr-header">
          <h1 className="tr-title">Token rate</h1>
          <p className="tr-subtitle">
            Read the live rate and publish updates to the contract. Decimal
            numbers you type are converted automatically.
          </p>
        </header>

        <div className="tr-toolbar">
          <Button
            variant="outline-secondary"
            className="d-inline-flex align-items-center gap-1"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <MdRefresh size={18} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-1"
            href={`${Data.testnetLink}${ContractDetails.contract}`}
            target="_blank"
            rel="noreferrer"
          >
            <MdOpenInNew size={18} />
            Block explorer
          </Button>
        </div>

        <div className="tr-grid">
          <article className="tr-card">
            <div className="tr-card__head">
              <h2 className="tr-card__head-title">
                <MdToken aria-hidden />
                Current rate
              </h2>
              <span className="tr-badge">On-chain</span>
            </div>
            <div className="tr-card__body">
              <div className="tr-rate-hero">
                <p className="tr-rate-label">Token rate</p>
                <p className="tr-rate-value">{displayCurrent}</p>
              </div>
            </div>
          </article>

          <article className="tr-card tr-form-card">
            <div className="tr-card__head">
              <h2 className="tr-card__head-title">
                <MdToken aria-hidden />
                {ownerMatch ? "Update rate" : "Contract owner"}
              </h2>
            </div>
            <div className="tr-card__body">
              {!ownerMatch && contractOwner ? (
                <div className="tr-not-owner">
                  <p className="tr-not-owner-title">Owner wallet only</p>
                  <p className="tr-not-owner-address">{contractOwner}</p>
                  <p className="tr-not-owner-hint">
                    Your connected wallet is not the owner. Rate changes are
                    disabled. Connect with the address above to update the rate.
                  </p>
                  <div className="tr-connected-wallet">
                    Connected:{" "}
                    <code>{account}</code>
                  </div>
                </div>
              ) : null}

              {!ownerMatch && !contractOwner ? (
                <p className="tr-hint">
                  Could not load contract owner. Try refresh or check the
                  network.
                </p>
              ) : null}

              {ownerMatch ? (
                <>
                  <div className="tr-field">
                    <label htmlFor="new-chain-rate">New rate</label>
                    <input
                      id="new-chain-rate"
                      className="tr-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.05"
                      value={newRateStr}
                      onChange={(e) => setNewRateStr(e.target.value)}
                      autoComplete="off"
                      disabled={savingRate}
                    />
                  </div>
                  <p className="tr-hint">
                    Enter a normal decimal. Behind the scenes we format it for
                    the contract (18 decimal precision).
                  </p>
                  <button
                    type="button"
                    className="tr-save"
                    onClick={handleSetTokenRate}
                    disabled={savingRate || !ownerMatch}
                  >
                    {savingRate ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                          role="status"
                        />
                        Saving…
                      </>
                    ) : (
                      "Save new rate"
                    )}
                  </button>
                </>
              ) : null}
            </div>
          </article>
        </div>
      </div>
    </Container>
  );
};

export default TokenRateOnChain;
